import { ACCESS_ANCHORS } from "#app/components/settings-search/rows/access";
import React from "react";
import {
  Alert,
  AlertDescription,
  Box,
  Button,
  Dialog,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
  Field,
  FieldLabel,
  PasswordInput,
  toastManager,
} from "@salestrak/ui";

import { useAuth } from "#app/lib/auth-context";
import * as AuthService from "#app/lib/auth-service";
import { SettingsGroup, SettingsGroupRow } from "#app/components/settings/settings-group";
import { SettingsGroupRowsSkeleton } from "#app/components/settings/settings-page-skeleton";

const FORM_ID = "change-password-form";

/**
 * The profile page's Security group: the password credential row — change
 * password when one exists, the emailed set-password flow when the account
 * signs in another way (e.g. Google). Sign-in sources come from the auth
 * service, not workspace data, so this only ever renders for self. While the
 * sources load, the group frame renders with one delayed skeleton row — the
 * group is always exactly one row, so the resolve swaps in place and the
 * sections below never reflow. A load failure shows a retry instead of
 * hiding, because this group holds the only change-password affordance in
 * the app and a silent hide reads as "password changes don't exist here".
 */
export function SecuritySettings() {
  const { getAccessToken } = useAuth();
  const [sources, setSources] = React.useState<string[] | null>(null);
  const [loadFailed, setLoadFailed] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [attempt, setAttempt] = React.useState(0);
  const [dialogOpen, setDialogOpen] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const token = await getAccessToken();
        // A momentarily null token is a failed load like any other — the group
        // must not go quiet over it.
        if (!token) throw new Error("Not authenticated");
        const loaded = await AuthService.fetchAuthSources(token);
        if (!cancelled) {
          setSources(loaded);
          setLoadFailed(false);
        }
      } catch {
        if (!cancelled) setLoadFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getAccessToken, attempt]);

  if (loadFailed) {
    return (
      <SettingsGroup label="Security">
        <SettingsGroupRow
          label="Password"
          subtitle="We couldn't load your sign-in methods."
        >
          <Box direction="row" justify="end">
            <Button
              variant="ghost"
              loading={loading}
              onClick={() => setAttempt((n) => n + 1)}
            >
              Try again
            </Button>
          </Box>
        </SettingsGroupRow>
      </SettingsGroup>
    );
  }

  if (!sources) {
    return (
      <SettingsGroup label="Security">
        <SettingsGroupRowsSkeleton rows={1} />
      </SettingsGroup>
    );
  }
  if (sources.length === 0) return null;
  const hasPassword = sources.includes("password");

  return (
    <>
      <SettingsGroup label="Security">
        <SettingsGroupRow
          highlightAnchor={ACCESS_ANCHORS.password}
          label="Password"
          subtitle={hasPassword ? undefined : "We'll email you a link to create one"}
        >
          <Box direction="row" justify="end">
            {hasPassword ? (
              <Button variant="ghost" onClick={() => setDialogOpen(true)}>
                Change password...
              </Button>
            ) : (
              <SetPasswordButton />
            )}
          </Box>
        </SettingsGroupRow>
      </SettingsGroup>
      {/* Outside the group: a SettingsGroup child earns a row separator, and a
        * closed dialog renders nothing in flow — inside the card that paints a
        * phantom divider under the last real row. */}
      {hasPassword ? (
        <ChangePasswordDialog open={dialogOpen} onOpenChange={setDialogOpen} />
      ) : null}
    </>
  );
}

/**
 * "Set password" for accounts with no password credential (e.g. Google
 * sign-ups). Sends the emailed set-password link — identity comes from the
 * session, so there is nothing to type. One send per page view; the settled
 * "Email sent" state stops accidental re-mails.
 */
function SetPasswordButton() {
  const { getAccessToken } = useAuth();
  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(false);

  const handleSend = async () => {
    setSending(true);
    try {
      const token = await getAccessToken();
      if (!token) throw new Error("Not authenticated");
      await AuthService.requestPasswordSetupEmail(token);
      setSent(true);
      toastManager.add({ title: "Password email sent", type: "success" });
    } catch {
      toastManager.add({ title: "Failed to send password email", type: "error" });
    } finally {
      setSending(false);
    }
  };

  return (
    <Button
      variant="ghost"
      loading={sending}
      disabled={sent}
      onClick={() => void handleSend()}
    >
      {sent ? "Email sent" : "Set password..."}
    </Button>
  );
}

function ChangePasswordDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { getAccessToken } = useAuth();
  const [currentPassword, setCurrentPassword] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [error, setError] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  // A reopened dialog starts clean — never with a half-typed password.
  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setError("");
    }
    onOpenChange(next);
  };

  const handleSave = async () => {
    setError("");
    const problem = AuthService.newPasswordProblem(newPassword);
    if (problem) {
      setError(problem);
      return;
    }
    if (newPassword === currentPassword) {
      setError("Choose a new password that is different from your current one.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("New passwords do not match.");
      return;
    }
    setSaving(true);
    try {
      const token = await getAccessToken();
      if (!token) throw new Error("Not authenticated");
      await AuthService.changePassword(token, currentPassword, newPassword);
      toastManager.add({ title: "Password changed", type: "success" });
      handleOpenChange(false);
    } catch (err: any) {
      setError(err.message ?? "Failed to change password. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void handleSave();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogPopup
        dialogOptions={{
          primary: { label: "Change password", form: FORM_ID, disabled: saving || !currentPassword.trim() || !newPassword || !confirmPassword.trim(), loading: saving },
          cancel: { disabled: saving },
        }}
      >
        <DialogHeader>
          <DialogTitle>Change password</DialogTitle>
        </DialogHeader>
        <DialogPanel>
          <Box
            as="form"
            id={FORM_ID}
            direction="column"
            gap="md"
            onSubmit={handleSubmit}
          >
            <Field>
              <FieldLabel>Current password</FieldLabel>
              <PasswordInput
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoFocus
                autoComplete="current-password"
                disabled={saving}
              />
            </Field>
            <Field>
              <FieldLabel>New password</FieldLabel>
              <PasswordInput
                placeholder={`At least ${AuthService.PASSWORD_MIN_LENGTH} characters`}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                disabled={saving}
              />
            </Field>
            <Field>
              <FieldLabel>Confirm new password</FieldLabel>
              <PasswordInput
                placeholder="Re-enter your new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                disabled={saving}
              />
            </Field>

            {error ? (
              <Alert variant="error">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}
          </Box>
        </DialogPanel>
        {/* The footer's primary action submits the form element above via the
            form attribute, so Enter in any field and the button share one
            submit path. */}
      </DialogPopup>
    </Dialog>
  );
}
